import { MessageSquare } from 'lucide-react';

import { PLATFORM_LINKS, RESOURCE_LINKS, SOCIAL_ICONS } from './landingPageSections';

const LOGO_SRC = '/assets/logo/waysprite_new_logo_cropped_transparent.svg';

interface LandingPageCtaProps {
  onPlayNow: () => void;
}

export function LandingPageCta({ onPlayNow }: LandingPageCtaProps): JSX.Element {
  return (
    <section className="relative overflow-hidden py-32">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-[#1d4ed8]/5 to-transparent" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#06b6d4]/6 blur-[100px]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 40px, #0ea5e9 40px, #0ea5e9 41px)',
        }}
      />
      <div className="mx-auto max-w-3xl px-5 text-center">
        <div className="mx-auto mb-8 h-16 w-16">
          <img alt="Waysprite" height={64} src={LOGO_SRC} width={64} />
        </div>
        <h2 className="mb-6 text-5xl font-black leading-tight tracking-tight text-white lg:text-6xl">
          Ready to enter
          <br />
          <span className="bg-gradient-to-r from-[#38bdf8] to-[#22d3ee] bg-clip-text text-transparent">
            the Waysprite world?
          </span>
        </h2>
        <p className="mb-10 text-xl leading-relaxed text-sky-400/80">
          Join thousands of players and creators building the first generation of AI-native
          multiplayer worlds.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <button
            className="w-full rounded-2xl bg-gradient-to-r from-[#1d4ed8] to-[#0ea5e9] px-10 py-4 text-lg font-black text-white shadow-2xl shadow-[#0ea5e9]/25 transition-all hover:from-[#2563eb] hover:to-[#38bdf8] sm:w-auto"
            onClick={onPlayNow}
            type="button"
          >
            Play for Free
          </button>
          <a
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#1e3a5f] bg-[#071428]/80 px-10 py-4 text-lg font-bold text-sky-200 backdrop-blur-sm transition-all hover:border-[#0ea5e9]/50 sm:w-auto"
            href="https://discord.gg/waysprite"
            rel="noopener noreferrer"
            target="_blank"
          >
            <MessageSquare className="h-5 w-5" />
            <span>Join Discord</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export function LandingPageFooter(): JSX.Element {
  return (
    <footer className="border-t border-[#0ea5e9]/10 bg-[#040810]/95 py-16 backdrop-blur-sm">
      <div className="mx-auto mb-12 grid max-w-7xl grid-cols-1 gap-10 px-5 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="mb-5 flex items-center gap-3">
            <img alt="Waysprite" height={32} src={LOGO_SRC} width={32} />
            <span className="text-lg font-black tracking-tight text-white">Waysprite</span>
          </div>
          <p className="mb-6 max-w-sm text-sm leading-relaxed text-sky-700">
            The premier platform for AI-native gaming. Built with Phaser 3, React 18, Firebase, and
            the world&apos;s most capable LLM providers.
          </p>
          <div className="flex gap-3">
            {SOCIAL_ICONS.map(({ Icon, name }) => (
              <button
                key={name}
                aria-label={name}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#1e3a5f]/70 bg-[#071428] transition-colors hover:border-[#0ea5e9]/50"
                type="button"
              >
                <Icon className="h-4 w-4 text-sky-600" />
              </button>
            ))}
          </div>
        </div>
        <div>
          <h4 className="mb-5 text-sm font-bold uppercase tracking-widest text-white">Platform</h4>
          <ul className="space-y-3">
            {PLATFORM_LINKS.map((link) => (
              <li key={link.label}>
                <span className="cursor-default text-sm text-sky-700 transition-colors hover:text-sky-200">
                  {link.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="mb-5 text-sm font-bold uppercase tracking-widest text-white">Resources</h4>
          <ul className="space-y-3">
            {RESOURCE_LINKS.map((link) => (
              <li key={link.label}>
                <span className="cursor-default text-sm text-sky-700 transition-colors hover:text-sky-200">
                  {link.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 border-t border-[#0ea5e9]/10 px-5 pt-8 md:flex-row">
        <p className="text-sm text-sky-800">
          &copy; {new Date().getFullYear()} Waysprite Labs. All rights reserved.
        </p>
        <div className="flex gap-6 text-sm text-sky-800">
          <span className="cursor-default transition-colors hover:text-sky-300">
            Privacy Policy
          </span>
          <span className="cursor-default transition-colors hover:text-sky-300">
            Terms of Service
          </span>
          <span className="cursor-default transition-colors hover:text-sky-300">
            Cookie Settings
          </span>
        </div>
      </div>
    </footer>
  );
}
